import classNames from "classnames";
import "./Sidebar.scss";
import {
  CSSProperties,
  PointerEvent,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";

const MIN_SIDEBAR_WIDTH = 300;
const DEFAULT_SIDEBAR_WIDTH = 450;

type Props = {
  enabled: boolean;
  isOpen: boolean;
  sidebarContent: ReactNode;
  children: ReactNode;
};

// The same elements are rendered whether or not the tools are available, and
// only the classes differ. That keeps page content in the same position in the
// React tree, so crossing the tools boundary never remounts the page under the
// user. Sidebar.scss styles the states.
//
// Resizing tracks the pointer via pointer capture on the splitter. While the
// drag is in flight the width is written straight to the drawer element's
// --sidebar-width custom property, bypassing React state so the whole page
// subtree isn't re-rendered per pointermove; the final width is committed to
// state once on release.
export default function Sidebar(props: Props) {
  const [width, setWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [isResizing, setIsResizing] = useState(false);

  const drawerRef = useRef<HTMLDivElement | null>(null);
  const liveWidthRef = useRef(width);

  const onSplitterPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizing(true);
  }, []);

  const onSplitterPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }
    const next = Math.max(MIN_SIDEBAR_WIDTH, window.innerWidth - event.clientX);
    liveWidthRef.current = next;
    drawerRef.current?.style.setProperty("--sidebar-width", `${next}px`);
  }, []);

  const onSplitterPointerEnd = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }
    setIsResizing(false);
    setWidth(liveWidthRef.current);
  }, []);

  const splitterProps = {
    onPointerDown: onSplitterPointerDown,
    onPointerMove: onSplitterPointerMove,
    onPointerUp: onSplitterPointerEnd,
    onPointerCancel: onSplitterPointerEnd,
  };

  // Mid-drag re-renders (e.g. a query settling) re-apply the drawer's style
  // prop; using the live value while resizing keeps them from snapping the
  // width back to the last committed state.
  const displayWidth = isResizing ? liveWidthRef.current : width;

  const drawerStyle = {
    "--sidebar-width": `${displayWidth}px`,
  } as CSSProperties;

  return (
    <div
      className={classNames("page-content-with-sidebar", {
        "dictionary-tools-enabled": props.enabled,
      })}
    >
      <div
        className={classNames("drawer-root", {
          open: props.isOpen,
          resizing: isResizing,
        })}
        style={drawerStyle}
        ref={drawerRef}
      >
        <div id="sidebar-id" role="navigation">
          <div className="sidebar-wrapper">
            <div className="splitter" {...splitterProps} />
            {/* A closed drawer stays mounted so it opens instantly and keeps its state. */}
            {props.enabled && props.sidebarContent}
          </div>
        </div>
        <div id="sidebar-content-id">{props.children}</div>
      </div>
    </div>
  );
}
